import {
  AgreementNo,
  AuthFill,
  Button,
  CommunicationsPreferences,
  ContactPreferences,
  Email,
  FirstName,
  LastName,
  PreferenceTerms,
} from '@components/form/fields'
import { FormStep, FormWrapper } from '@components/form/layout'
import { Nav } from '@components/ui'
import { useCampaign } from '@contexts/index'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import { closeOverlay, getPageData } from '@utils/helpers'
import { formEvent } from '@utils/helpers/tracking'
import { MarketingPreferencesSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { upperCaseFirst } from 'upper-case-first'

const TFS = ({ content, tracking, brand, query }) => {
  const campaign = useCampaign()
  const [submitResponse, setSubmitResponse] = useState('')

  const initialValues = {
    first_name: '',
    last_name: '',
    email: '',
    confirm_email: '',
    agreement_or_reg_num: '',
    communication_relevantoffers: '',
    contact_phone: '',
    contact_email: '',
    contact_sms: '',
    contact_post: '',
    consent: {
      user: false,
    },
    unsubscribe: [],
    response_code: '',
    campaign_response_code: '',
    campaign_code: '',
    is_lexus: brand === 'lexus',
  }

  const methods = useForm({
    resolver: yupResolver(MarketingPreferencesSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  useEffect(() => {
    if (campaign?.campaign) {
      methods.setValue('campaign_code', campaign.campaign)
    }

    if (campaign?.media) {
      const code = methods.getValues('campaign_code')
      methods.setValue('campaign_code', code + campaign.media)
    }

    if (campaign?.brochure) {
      methods.setValue('campaign_response_code', campaign.brochure)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign])

  useEffect(() => {
    if (query?.email) {
      methods.setValue('email', query.email)
      methods.setValue('confirm_email', query.email)
    }

    if (query?.title) {
      methods.setValue('title', query.title)
    }

    if (query?.firstname || query?.first_name) {
      methods.setValue('first_name', query.firstname || query.first_name)
    }

    if (query?.lastname || query?.last_name) {
      methods.setValue('last_name', query.lastname || query.last_name)
    }
  }, [query])

  const onSubmit = async (data) => {
    const formData = data

    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/marketing-preferences`,
      headers: {
        'Content-Type': 'application/json',
      },
      data: formData,
    })
      .then(() => {
        formEvent({
          conversion: 1,
          name: tracking.form,
          step: 'form-submit',
          variant: '',
          page: tracking.page,
        })
        closeOverlay(
          content.confirmation.title,
          content.confirmation.copy,
          setSubmitResponse,
        )
      })
      .catch(() => {
        formEvent({
          conversion: 0,
          name: tracking.form,
          step: 'form-error',
          variant: '',
          page: tracking.page,
        })
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  return (
    <>
      <Nav
        title={content.title}
        logoType={content?.logotype}
        customLogo={content?.customlogo}
      />
      <FormWrapper>
        <FormStep
          title={content.details.title}
          subtitle={content.details.subtitle}
          step={1}
          active={true}
        >
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <AuthFill />
              <FirstName />
              <LastName />
              <Email />
              <AgreementNo label="Agreement / Proposal Number " />
              <CommunicationsPreferences />
              <ContactPreferences />
              <PreferenceTerms
                preferenceType={`${upperCaseFirst(brand)} Financial Services`}
              />
              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'tfs'

  const content = await import(
    `../../content/${brand}/forms/marketing-preferences/${form}.md`
  ).catch(() => null)
  const settings = await import(
    `../../content/${brand}/settings/global.md`
  ).catch(() => null)

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }

  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  return {
    props: {
      content: content.attributes,
      title: content.attributes.title,
      tracking: {
        page: 'preferences-tfs',
        form: 'preferences-tfs',
        variant: '',
        initialStep: 'contact-details',
      },
      description: '',
      ...(await getPageData(context)),
    },
  }
}

TFS.whyDidYouRender = true

export default TFS
